import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, CheckCircle, Info, AlertTriangle, MoreVertical, X, type LucideIcon } from 'lucide-react';

/**
 * Sperrt das Scrollen des Hintergrunds, solange ein Overlay offen ist.
 * Zaehlt offene Overlays mit, damit verschachtelte Sheets sich nicht gegenseitig entsperren.
 */
let scrollLockCount = 0;

export const useBodyScrollLock = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    scrollLockCount += 1;
    document.body.classList.add('overflow-locked');
    return () => {
      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) {
        document.body.classList.remove('overflow-locked');
      }
    };
  }, [active]);
};

/** Schliesst ein Overlay per Escape-Taste. */
export const useEscapeKey = (active: boolean, onEscape: () => void) => {
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onEscape();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [active, onEscape]);
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}) => {
  const baseClasses = 'btn transition-all duration-200 focus:outline-none';

  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95',
    secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 active:scale-95',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:scale-95',
    // Gleiche Optik wie die direkt per Klasse gesetzten btn-ghost-Buttons (inkl. Hell-Theme-Variante in index.css)
    ghost: 'btn-ghost',
  };

  const sizeClasses = {
    sm: 'px-3 py-1 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  return (
    <button
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        (disabled || loading) && 'opacity-50 cursor-not-allowed',
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      <span className="flex items-center justify-center gap-2">
        {loading && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
        {!loading && icon}
        {children}
      </span>
    </button>
  );
};

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  icon,
  fullWidth = true,
  className,
  id,
  ...props
}) => {
  const generatedId = useId();
  const inputId = id || `input-${generatedId}`;

  return (
    <div className={fullWidth ? 'w-full' : undefined}>
      {label && (
        <label htmlFor={inputId} className="label-base">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          className={clsx('input-base', error && 'border-red-500', className)}
          {...props}
        />
        {icon && <div className="absolute right-4 top-1/2 -translate-y-1/2">{icon}</div>}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
}

export const Textarea: React.FC<TextareaProps> = ({
  label,
  error,
  fullWidth = true,
  className,
  id,
  ...props
}) => {
  const generatedId = useId();
  const textareaId = id || `textarea-${generatedId}`;

  return (
    <div className={fullWidth ? 'w-full' : undefined}>
      {label && (
        <label htmlFor={textareaId} className="label-base">
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        className={clsx('input-base textarea-base', error && 'border-red-500', className)}
        {...props}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: Array<{ value: string | number; label: string }>;
  fullWidth?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  options,
  fullWidth = true,
  className,
  id,
  ...props
}) => {
  const generatedId = useId();
  const selectId = id || `select-${generatedId}`;

  return (
    <div className={fullWidth ? 'w-full' : undefined}>
      {label && (
        <label htmlFor={selectId} className="label-base">
          {label}
        </label>
      )}
      <select
        id={selectId}
        className={clsx('input-base', error && 'border-red-500', className)}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  onClick?: () => void;
  /** Aktionsleiste am unteren Kartenrand: buendig bis zur Kante, per Trennlinie vom Inhalt
   *  abgesetzt und innerhalb der Kartenrundung - analog zum Footer im Sheet. */
  footer?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  hover = false,
  onClick,
  footer,
}) => {
  return (
    <div
      className={clsx('card', hover && 'card-hover', onClick && 'cursor-pointer', className)}
      onClick={onClick}
    >
      {children}
      {footer && <div className="card-footer">{footer}</div>}
    </div>
  );
};

export interface CardAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Aktionsleiste fuer den Footer einer Card: die Aktionen teilen sich randlos eine Zeile ueber
 * die volle Kartenbreite, getrennt durch senkrechte Linien. Unter sm steht das Label unter dem
 * Icon, damit auch drei Aktionen nebeneinander lesbar bleiben.
 */
export const CardActions: React.FC<{ actions: CardAction[] }> = ({ actions }) => (
  <div className="card-actions">
    {actions.map((action) => (
      <button
        key={action.label}
        type="button"
        className={clsx('card-action', action.variant === 'danger' && 'card-action-danger')}
        disabled={action.disabled || action.loading}
        onClick={action.onClick}
      >
        {action.loading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          action.icon
        )}
        <span className="card-action-label">{action.label}</span>
      </button>
    ))}
  </div>
);

type AlertType = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  type: AlertType;
  title?: string;
  message: string;
  onClose?: () => void;
}

const AlertIcon = ({ type }: { type: AlertType }) => {
  const iconClass = 'w-5 h-5';
  switch (type) {
    case 'success':
      return <CheckCircle className={`${iconClass} text-green-400`} />;
    case 'error':
      return <AlertCircle className={`${iconClass} text-red-400`} />;
    case 'warning':
      return <AlertTriangle className={`${iconClass} text-accent-400`} />;
    case 'info':
      return <Info className={`${iconClass} text-secondary-400`} />;
  }
};

export const Alert: React.FC<AlertProps> = ({ type, title, message, onClose }) => {
  const bgColor = {
    success: 'bg-green-500/10 border-green-500/30',
    error: 'bg-red-500/10 border-red-500/30',
    warning: 'bg-accent-500/10 border-accent-500/30',
    info: 'bg-secondary-500/10 border-secondary-500/30',
  };

  const textColor = {
    success: 'text-green-400',
    error: 'text-red-400',
    warning: 'text-accent-400',
    info: 'text-secondary-400',
  };

  return (
    <div className={`border rounded-lg p-4 flex gap-3 ${bgColor[type]} animate-slideInUp`}>
      <AlertIcon type={type} />
      <div className="flex-1">
        {title && <h3 className={`font-semibold ${textColor[type]} mb-1`}>{title}</h3>}
        <p className="text-sm text-dark-300">{message}</p>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="text-dark-400 hover:text-dark-300 transition-colors"
        >
          {'\u00D7'}
        </button>
      )}
    </div>
  );
};

interface BadgeProps {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  children: React.ReactNode;
  size?: 'sm' | 'md';
  title?: string;
}

export const Badge: React.FC<BadgeProps> = ({ variant = 'primary', children, size = 'md', title }) => {
  const variantClasses = {
    primary: 'badge-primary',
    secondary: 'badge-secondary',
    success: 'badge-success',
    warning: 'badge-warning',
    error: 'badge-error',
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1',
  };

  return (
    <span className={clsx('badge', variantClasses[variant], sizeClasses[size])} title={title}>
      {children}
    </span>
  );
};

export type IconBadgeTone = 'primary' | 'secondary' | 'success' | 'warning' | 'error';

interface IconBadgeProps {
  icon: React.ReactNode;
  tone?: IconBadgeTone;
  size?: 'sm' | 'md';
}

const ICON_BADGE_TONE_CLASS: Record<IconBadgeTone, string> = {
  primary: 'badge-primary',
  secondary: 'badge-secondary',
  success: 'badge-success',
  warning: 'badge-warning',
  error: 'badge-error',
};

/** Getoenter Icon-Kreis fuer Sektionskoepfe - nutzt dieselben Farb-Tokens wie Badge (inkl. der
 * Hell-Theme-Kontrast-Fixes), statt eine eigene Palette zu erfinden. */
export const IconBadge: React.FC<IconBadgeProps> = ({ icon, tone = 'primary', size = 'md' }) => (
  <span
    className={clsx(
      'inline-flex shrink-0 items-center justify-center rounded-full',
      size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
      ICON_BADGE_TONE_CLASS[tone]
    )}
  >
    {icon}
  </span>
);

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
}

export const Loading: React.FC<LoadingProps> = ({ size = 'md', message }) => {
  const { t } = useTranslation();
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };
  const resolvedMessage = message ?? t('common:status.loading');

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className={clsx('border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin', sizeClasses[size])} />
      {resolvedMessage && <p className="text-dark-400">{resolvedMessage}</p>}
    </div>
  );
};

interface SheetProps {
  isOpen: boolean;
  title?: string;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const sheetSizeClasses = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-3xl',
  /** Fuer Inhalte, die von jedem Pixel profitieren (z.B. die vergroesserte Poster-Vorschau). */
  full: 'sm:max-w-[min(96rem,95vw)]',
};

/**
 * Overlay-Dialog. Auf dem Handy faehrt er als Bottom-Sheet mit eigenem Scrollbereich
 * hoch (lange Formulare bleiben so komplett erreichbar), ab sm ist es ein zentrierter Dialog.
 */
export const Sheet: React.FC<SheetProps> = ({ isOpen, title, children, onClose, footer, size = 'md' }) => {
  const { t } = useTranslation();
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  // Per Portal an <body>: Vorfahren mit backdrop-filter (z.B. .card, .glass) erzeugen sonst
  // einen eigenen Containing Block und das "fixed" Overlay klebt an der Karte statt am Viewport.
  return createPortal(
    <div className="sheet-overlay">
      <div className="anim-fade absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx('sheet-panel', sheetSizeClasses[size])}
      >
        <div className="sheet-header">
          <div className="pb-2 sm:hidden">
            <div className="sheet-handle" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <h2 className="min-w-0 truncate text-lg font-bold text-dark-50">{title}</h2>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common:actions.close')}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="sheet-body">{children}</div>

        {footer && <div className="sheet-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

/** Beibehaltener Name fuer bestehende Aufrufstellen. */
export const Modal: React.FC<SheetProps> = (props) => <Sheet {...props} />;

interface ActionSheetAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface ActionSheetProps {
  isOpen: boolean;
  title?: string;
  actions: ActionSheetAction[];
  onClose: () => void;
}

/**
 * Aktionsliste als Sheet. Ersetzt auf Mobile Reihen winziger Icon-Buttons:
 * jede Aktion ist beschriftet und hat ein volles Touch-Ziel.
 */
export const ActionSheet: React.FC<ActionSheetProps> = ({ isOpen, title, actions, onClose }) => (
  <Sheet isOpen={isOpen} title={title} onClose={onClose} size="sm">
    <div className="flex flex-col gap-1">
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          disabled={action.disabled}
          onClick={() => {
            onClose();
            action.onClick();
          }}
          className={clsx(
            'flex min-h-[3rem] items-center gap-3 rounded-xl px-3 py-2 text-left text-[0.9375rem] font-medium transition-colors',
            action.variant === 'danger'
              ? 'text-red-300 hover:bg-red-500/10'
              : 'text-dark-100 hover:bg-dark-700',
            action.disabled && 'pointer-events-none opacity-40'
          )}
        >
          {action.icon && <span className="shrink-0 text-dark-400">{action.icon}</span>}
          {action.label}
        </button>
      ))}
    </div>
  </Sheet>
);

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  tone?: 'default' | 'primary' | 'danger';
  children: React.ReactNode;
}

/** Icon-Button mit 44px-Touch-Ziel und Pflicht-Label fuer Screenreader/Tooltip. */
export const IconButton: React.FC<IconButtonProps> = ({ label, tone = 'default', children, className, ...props }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    className={clsx(
      'icon-btn',
      tone === 'primary' && 'icon-btn-primary',
      tone === 'danger' && 'icon-btn-danger',
      className
    )}
    {...props}
  >
    {children}
  </button>
);

export interface RowAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  tone?: 'default' | 'primary' | 'danger';
  disabled?: boolean;
}

interface RowActionsProps {
  actions: RowAction[];
  /** Titel des Aktionen-Sheets auf dem Handy (z.B. der Name des Eintrags). */
  title?: string;
}

/**
 * Aktionen einer Listenzeile. Ab sm als Reihe beschrifteter Icon-Buttons, auf dem Handy als ein
 * einziger Menue-Button mit Aktions-Sheet - drei bis vier Icon-Buttons nebeneinander frassen dort
 * die halbe Zeilenbreite, und der eigentliche Inhalt der Zeile wurde abgeschnitten.
 */
export const RowActions: React.FC<RowActionsProps> = ({ actions, title }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (actions.length === 0) return null;
  // Eine einzelne Aktion braucht kein Menue.
  if (actions.length === 1) {
    const [only] = actions;
    return (
      <IconButton label={only.label} tone={only.tone} disabled={only.disabled} onClick={only.onClick}>
        <only.icon size={16} />
      </IconButton>
    );
  }

  return (
    <>
      <div className="shrink-0 sm:hidden">
        <IconButton label={t('common:actions.more')} onClick={() => setOpen(true)}>
          <MoreVertical size={18} />
        </IconButton>
      </div>
      <div className="hidden shrink-0 gap-0.5 sm:flex">
        {actions.map((action) => (
          <IconButton key={action.label} label={action.label} tone={action.tone} disabled={action.disabled} onClick={action.onClick}>
            <action.icon size={16} />
          </IconButton>
        ))}
      </div>
      <ActionSheet
        isOpen={open}
        title={title}
        onClose={() => setOpen(false)}
        actions={actions.map((action) => ({
          label: action.label,
          icon: <action.icon size={18} />,
          variant: action.tone === 'danger' ? ('danger' as const) : ('default' as const),
          disabled: action.disabled,
          onClick: action.onClick
        }))}
      />
    </>
  );
};

export interface SegmentedTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
}

interface SegmentedTabsProps {
  tabs: SegmentedTab[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

/** Umschalter zwischen Seitenbereichen - ersetzt endloses Scrollen durch gestapelte Sektionen. */
export const SegmentedTabs: React.FC<SegmentedTabsProps> = ({ tabs, value, onChange, className }) => {
  const stripRef = useRef<HTMLDivElement>(null);

  // Auf schmalen Screens scrollt die Leiste waagerecht: der gewaehlte Tab (auch per URL oder
  // Link vorgewaehlt) soll sichtbar sein, statt hinter dem Rand zu verschwinden. Nur die Leiste
  // scrollt, nicht die Seite - deshalb kein scrollIntoView.
  useEffect(() => {
    const strip = stripRef.current;
    const selected = strip?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!strip || !selected || strip.scrollWidth <= strip.clientWidth) return;
    const target = selected.offsetLeft - (strip.clientWidth - selected.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [value]);

  return (
    <div ref={stripRef} className={clsx('segmented hide-scrollbar', className)} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={value === tab.id}
          className="segmented-item"
          onClick={() => onChange(tab.id)}
        >
          {tab.icon}
          <span>{tab.label}</span>
          {typeof tab.count === 'number' && tab.count > 0 && (
            <span
              className={clsx(
                'rounded-full px-1.5 text-xs font-semibold',
                value === tab.id ? 'bg-white/20 text-white' : 'bg-dark-700 text-dark-300'
              )}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = '\u{1F4ED}',
  title,
  description,
  action,
}) => {
  return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-dark-400 mb-6">{description}</p>
      {action && (
        <Button onClick={action.onClick} variant="primary">
          {action.label}
        </Button>
      )}
    </div>
  );
};

export const PageBackdrop: React.FC = () => (
  <div className="page-backdrop pointer-events-none fixed inset-0 overflow-hidden">
    <div className="absolute -top-28 -left-24 h-80 w-80 rounded-full bg-primary-600/25 blur-3xl" />
    <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-secondary-600/20 blur-3xl" />
    <div className="absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-accent-600/10 blur-3xl" />
  </div>
);

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /** Der Zurueck-Weg, benannt nach seinem Ziel ("Cockpit", "Nutzer", ...). */
  backLink: { label: string; onClick: () => void };
}

/**
 * Seitenkopf ohne eigenes Panel: der Zurueck-Weg steht als Textlink ueber dem Titel und nennt
 * sein Ziel, statt als Pfeil im Kasten daneben zu sitzen. Dadurch traegt die Seite nur noch die
 * Flaeche des Inhalts statt zweier gestapelter, und eine Badge eruebrigt sich - der Link nennt
 * den Bereich bereits.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions, children, backLink }) => {
  useEffect(() => {
    document.title = title ? `canebase – ${title}` : 'canebase';
  }, [title]);

  return (
    <header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div className="min-w-0">
          {/* py-3 statt eines Rahmens: die Trefferflaeche erreicht damit die empfohlenen 44px,
              ohne dass der Link optisch wieder zum Kasten wird. Flaeche erst beim Hover. */}
          <button
            type="button"
            onClick={backLink.onClick}
            className="-ml-2 inline-flex max-w-full items-center gap-1.5 rounded-lg px-2 py-3 text-sm font-medium text-dark-300 transition-colors hover:bg-dark-700/50 hover:text-dark-100"
          >
            <ArrowLeft size={15} className="shrink-0" />
            <span className="truncate">{backLink.label}</span>
          </button>
          {/* break-words, damit lange Titel ohne Trennstelle (z.B. Task-Typen in CamelCase)
              umbrechen statt am Rand abgeschnitten zu werden */}
          <h1 className="mt-1 break-words text-2xl font-bold tracking-tight text-dark-50">{title}</h1>
          {subtitle && <p className="mt-1 break-words text-sm text-dark-300">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2 sm:gap-3">{actions}</div>}
      </div>
      {children}
    </header>
  );
};
